/**
 * Kiosk settings metadata: which settings exist, how each is presented,
 * which editor it gets, and which level a change writes to. Shared by the
 * Settings panel (full editor) and the Kiosk Settings summary card.
 */

import dayjs from 'dayjs';

import { TrackingFeature } from '@api/org';
import { CUT_FEE_MAP, WARRANTY_PLAN_MAP, UI_SELECTION_MAP } from '@consts/company';

import type { SelectOption } from '@components/design-system';
import type { BillingType, WarrantyType, UISelection } from '@consts/company';

export enum SettingKey {
    WalletTemplate = 'walletTemplate',
    UiSelection = 'uiSelection',
    ProductCodeEntry = 'productCodeEntryEnabled',
    Banners = 'banners',
    CutPlan = 'cutPlan',
    WarrantyPlan = 'warrantyPlan',
    RepTracking = 'repTracking',
    DeviceId = 'deviceIdEnabled',
    WatchDuplicate = 'watchDuplicateEnabled',
    BladeTracking = 'bladeTracking',
    BladeCadenceCuts = 'bladeCadenceCuts',
    BladeCountingSince = 'bladeCountingSince',
    KitTracking = 'kitTracking',
    KitCadenceCuts = 'kitCadenceCuts',
}

/** Which level a change to the setting writes to. */
export enum SettingScope {
    Station = 'station',
    Location = 'location',
    Org = 'org',
    Tracking = 'tracking',
}

export enum SettingEditor {
    Toggle = 'toggle',
    Select = 'select',
    Cadence = 'cadence',
    Date = 'date',
}

export type EditableSettingConfig = {
    editor: SettingEditor;
    scope: SettingScope;
    /** Station PATCH field, for SettingScope.Station keys. */
    stationField?: 'UISelectionID' | 'WarrantyPlanID' | 'ProductCodeEntryEnabled';
    /** Company/Reseller PATCH field, for SettingScope.Org keys. */
    orgField?: 'SalesRepTrackingEnabled' | 'DeviceIdEnabled' | 'WatchDuplicateEnabled';
    /** Tracking PUT feature + field, for SettingScope.Tracking keys. */
    trackingFeature?: TrackingFeature;
    trackingField?: 'Enabled' | 'CadenceCuts' | 'StartDate';
    /** Static select options; wallet template options come from the brands query instead. */
    options?: SelectOption[];
};

const toOptions = (map: Record<number, string>): SelectOption[] =>
    Object.entries(map).map(([value, label]) => ({ value: +value, label }));

export const EDITABLE_SETTINGS: Partial<Record<SettingKey, EditableSettingConfig>> = {
    [SettingKey.UiSelection]: {
        editor: SettingEditor.Select,
        scope: SettingScope.Station,
        stationField: 'UISelectionID',
        options: toOptions(UI_SELECTION_MAP),
    },
    [SettingKey.ProductCodeEntry]: {
        editor: SettingEditor.Toggle,
        scope: SettingScope.Station,
        stationField: 'ProductCodeEntryEnabled',
    },
    [SettingKey.WarrantyPlan]: {
        editor: SettingEditor.Select,
        scope: SettingScope.Station,
        stationField: 'WarrantyPlanID',
        options: toOptions(WARRANTY_PLAN_MAP),
    },
    [SettingKey.WalletTemplate]: {
        editor: SettingEditor.Select,
        scope: SettingScope.Location,
    },
    [SettingKey.RepTracking]: {
        editor: SettingEditor.Toggle,
        scope: SettingScope.Org,
        orgField: 'SalesRepTrackingEnabled',
    },
    [SettingKey.DeviceId]: {
        editor: SettingEditor.Toggle,
        scope: SettingScope.Org,
        orgField: 'DeviceIdEnabled',
    },
    [SettingKey.WatchDuplicate]: {
        editor: SettingEditor.Toggle,
        scope: SettingScope.Org,
        orgField: 'WatchDuplicateEnabled',
    },
    [SettingKey.BladeTracking]: {
        editor: SettingEditor.Toggle,
        scope: SettingScope.Tracking,
        trackingFeature: TrackingFeature.Blade,
        trackingField: 'Enabled',
    },
    [SettingKey.BladeCadenceCuts]: {
        editor: SettingEditor.Cadence,
        scope: SettingScope.Tracking,
        trackingFeature: TrackingFeature.Blade,
        trackingField: 'CadenceCuts',
    },
    [SettingKey.BladeCountingSince]: {
        editor: SettingEditor.Date,
        scope: SettingScope.Tracking,
        trackingFeature: TrackingFeature.Blade,
        trackingField: 'StartDate',
    },
    [SettingKey.KitTracking]: {
        editor: SettingEditor.Toggle,
        scope: SettingScope.Tracking,
        trackingFeature: TrackingFeature.Kit,
        trackingField: 'Enabled',
    },
    [SettingKey.KitCadenceCuts]: {
        editor: SettingEditor.Cadence,
        scope: SettingScope.Tracking,
        trackingFeature: TrackingFeature.Kit,
        trackingField: 'CadenceCuts',
    },
};

/** Why a row has no editor even for super admins. */
export const READ_ONLY_NOTES: Partial<Record<SettingKey, string>> = {
    [SettingKey.CutPlan]: 'Changed through the billing plan on the system page',
    [SettingKey.Banners]: 'Managed in the banner editors',
};

/** Shown on the counting-since row when this kiosk has a logged blade change, which outranks the org start date. */
export const BLADE_CHANGE_NOTE = 'Set by the last logged blade change on this kiosk';

/** Sub-settings that only apply (and only display) while their tracking flag is effectively on. */
export const TRACKING_SUB_SETTINGS: Partial<Record<SettingKey, SettingKey[]>> = {
    [SettingKey.BladeTracking]: [SettingKey.BladeCadenceCuts, SettingKey.BladeCountingSince],
    [SettingKey.KitTracking]: [SettingKey.KitCadenceCuts],
};

/** Grouped presentation order; sub-rows indent under their parent when present. */
export const SETTING_SECTIONS: { title: string; rows: { key: SettingKey; subRows?: SettingKey[] }[] }[] = [
    {
        title: 'Kiosk',
        rows: [
            { key: SettingKey.WalletTemplate },
            { key: SettingKey.UiSelection },
            { key: SettingKey.ProductCodeEntry },
            { key: SettingKey.Banners },
        ],
    },
    {
        title: 'Billing',
        rows: [{ key: SettingKey.CutPlan }, { key: SettingKey.WarrantyPlan }],
    },
    {
        title: 'Features',
        rows: [{ key: SettingKey.RepTracking }, { key: SettingKey.DeviceId }, { key: SettingKey.WatchDuplicate }],
    },
    {
        title: 'Consumables',
        rows: [
            { key: SettingKey.BladeTracking, subRows: [SettingKey.BladeCadenceCuts, SettingKey.BladeCountingSince] },
            { key: SettingKey.KitTracking, subRows: [SettingKey.KitCadenceCuts] },
        ],
    },
];

const formatCount = (value: number, noun: string): string => `${value} ${noun}${value === 1 ? '' : 's'}`;

/** Per-setting value presentation; anything unlisted falls back to String(value). */
const VALUE_FORMATTERS: Partial<Record<SettingKey, (value: string | number) => string>> = {
    [SettingKey.CutPlan]: (value) => CUT_FEE_MAP[value as BillingType] ?? `Plan ${value}`,
    [SettingKey.WarrantyPlan]: (value) => WARRANTY_PLAN_MAP[value as WarrantyType] ?? `Plan ${value}`,
    [SettingKey.UiSelection]: (value) => UI_SELECTION_MAP[value as UISelection] ?? `Selection ${value}`,
    // Calendar date stored as UTC midnight: format the date part, not the instant
    [SettingKey.BladeCountingSince]: (value) => dayjs(String(value).slice(0, 10)).format('MMM D, YYYY'),
    [SettingKey.BladeCadenceCuts]: (value) => `${Number(value).toLocaleString()} cuts`,
    [SettingKey.KitCadenceCuts]: (value) => `${Number(value).toLocaleString()} cuts`,
    [SettingKey.Banners]: (value) => formatCount(Number(value), 'banner'),
};

export const formatSettingValue = (key: SettingKey, value: string | number | boolean | null): string => {
    if (value === null || value === '') return 'Not set';
    if (typeof value === 'boolean') return value ? 'On' : 'Off';
    return VALUE_FORMATTERS[key]?.(value) ?? String(value);
};

/** Cadence input must be a positive whole number of cuts. */
export const cadenceValid = (value: string): boolean => /^\d+$/.test(value.trim()) && Number(value) > 0;

/** Parseable and not in the future; backdating is allowed (counts existing cuts). */
export const startDateValid = (value: string): boolean => {
    const parsed = dayjs(value, 'YYYY-MM-DD');
    return parsed.isValid() && !parsed.isAfter(dayjs(), 'day');
};
